import {
  FormControl, InputLabel, MenuItem, Select, SelectChangeEvent, Skeleton, styled, Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Plan } from '../../models';
import { SwitchService } from '../../services';
import Chart from './Chart';
import { BodyTypography } from '../shared/Styles';

const ChartSectionContainer = styled('div')`
  padding-top: 25px;
`;

type Props = {
  stateCode: string;
  selectedPlan: string;
  passedChartData?: Plan;
}

function UsageChargePeriod({ stateCode, selectedPlan, passedChartData }: Props) {
  const [chartData, setChartData] = useState<Plan>();
  const [selectedOption, setSelectedOption] = useState('');
  const [optionsList, setOptionsList] = useState<string[]>();
  const [isLoading, setIsLoading] = useState(false);
  const intl = useIntl();

  useEffect(() => {
    async function getChartData() {
      setIsLoading(true);
      try {
        const response = await SwitchService.getPlanChartData(stateCode, selectedPlan);
        setChartData(response);
        const optionsLabels = response.options.map((opt) => opt.optionLabel);
        setSelectedOption(optionsLabels[0]);
        setOptionsList(optionsLabels);
      } finally {
        setIsLoading(false);
      }
    }
    if (passedChartData) {
      setChartData(passedChartData);
      const optionsLabels = passedChartData.options.map((opt) => opt.optionLabel);
      setSelectedOption(optionsLabels[0]);
      setOptionsList(optionsLabels);
    } else {
      getChartData();
    }
  }, [passedChartData, selectedPlan, stateCode]);

  const selectChangeHandler = useCallback(
    (event: SelectChangeEvent<string>) => {
      setSelectedOption(`${event.target.value}`);
    },
    [],
  );

  return (
    <div>
      <Typography variant="h5" gutterBottom sx={{ marginTop: '15px' }} id="UsageHeader">
        <FormattedMessage
          id={`${selectedPlan}-plan-label`}
        />
      </Typography>
      <BodyTypography variant="body1">
        <FormattedMessage
          id="confirm-plan-charts.p1"
          values={{
            planCode: <FormattedMessage
              id={`${selectedPlan}-plan-label`}
            />,
          }}
        />
      </BodyTypography>
      <FormControl fullWidth>
        {(!isLoading && chartData) && (
          <InputLabel id="usage-select-label">
            <FormattedMessage id="confirm-plan-charts.dropdown-header" />
          </InputLabel>
        )}
        <Select
          data-testid="usageDropdown"
          labelId="UsageHeader"
          id="usage-select"
          value={selectedOption}
          label={intl.formatMessage({ id: 'confirm-plan-charts.dropdown-header' })}
          onChange={selectChangeHandler}
          defaultValue=""
          displayEmpty
          sx={{
            '& .MuiOutlinedInput-input.MuiSelect-select':
              { whiteSpace: 'break-spaces' },
          }}
        >
          {!isLoading ? (optionsList?.map((opt) => (
            <MenuItem
              style={{ whiteSpace: 'normal' }}
              value={opt}
              key={opt}
              data-testid="usageOption"
            >
              <FormattedMessage
                id={opt}
              />
            </MenuItem>
          ))) : (
            <MenuItem
              style={{ whiteSpace: 'normal' }}
              value=""
              key="loading"
              data-testid="usageOption"
            >
              <Skeleton variant="text" sx={{ fontSize: '1rem' }} />
            </MenuItem>
          )}
        </Select>
      </FormControl>
      <ChartSectionContainer data-testid="confirm-bar-chart">
        {chartData && <Chart chartData={chartData} selectedOption={selectedOption} />}
      </ChartSectionContainer>
    </div>
  );
}

UsageChargePeriod.defaultProps = {
  passedChartData: undefined,
};

export default UsageChargePeriod;
